import React from 'react';
import { Card, Descriptions, Spin, Empty } from 'antd';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { getAlertById } from '@/api/alerts';
import { useAuth } from '@/contexts/AuthContext';
import dayjs from 'dayjs';


interface AlertTabProps {
  alertId: string;
}

const AlertTab: React.FC<AlertTabProps> = ({ alertId }) => {
  const { t } = useTranslation(['alerts', 'common']);
  const { firestoreUser } = useAuth();

  const { data: alert, isLoading, error } = useQuery({
    queryKey: ['alert', alertId],
    queryFn: () => getAlertById(alertId, firestoreUser!),
    enabled: !!alertId && !!firestoreUser,
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <Card>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  if (error || !alert) {
    return (
      <Card>
        <Empty description={t('alerts:notFound')} />
      </Card>
    );
  }

  return (
    <Card>
      <Descriptions bordered column={1}>
        <Descriptions.Item label={t('alerts:fields.title')}>
          {alert.title}
        </Descriptions.Item>
        
        <Descriptions.Item label={t('alerts:fields.description')}>
          {alert.description}
        </Descriptions.Item>
        
        <Descriptions.Item label={t('alerts:fields.type')}>
          {t(`alerts:types.${alert.type}`)}
        </Descriptions.Item>
        
        <Descriptions.Item label={t('alerts:fields.status')}>
          {t(`alerts:status.${alert.status}`)}
        </Descriptions.Item>
        
        <Descriptions.Item label={t('alerts:fields.startTime')}>
          {dayjs(alert.startTime).format('DD/MM/YYYY HH:mm')}
        </Descriptions.Item>
        
        {alert.endTime && (
          <Descriptions.Item label={t('alerts:fields.endTime')}>
            {dayjs(alert.endTime).format('DD/MM/YYYY HH:mm')}
          </Descriptions.Item>
        )}
        
        {alert.block && (
          <Descriptions.Item label={t('alerts:fields.block')}>
            {alert.block.name}
          </Descriptions.Item>
        )}
        
        {alert.apartmentUnit && (
          <Descriptions.Item label={t('alerts:fields.apartmentUnit')}>
            {alert.apartmentUnit.name}
          </Descriptions.Item>
        )}
        
        {alert.amenity && (
          <Descriptions.Item label={t('alerts:fields.amenity')}>
            {alert.amenity.name}
          </Descriptions.Item>
        )}
        
        {alert.asset && (
          <Descriptions.Item label={t('alerts:fields.asset')}>
            {alert.asset.name}
          </Descriptions.Item>
        )}
        
        {alert.attachments && alert.attachments.length > 0 && (
          <Descriptions.Item label={t('alerts:fields.attachments')}>
            {alert.attachments.length} {t('common:items')}
          </Descriptions.Item>
        )}
      </Descriptions>
    </Card>
  );
};

export default AlertTab; 